import { IProblem, ProblemType } from "@/modules/a.worksheets/stores/worksheetDataStore";

type StudentPair = { element: string | number; student_choice: string; isCorrect?: boolean };
type OrderItem = { uuid: string; order?: number; isCorrect?: boolean };

export interface IOfflineScore {
  grade: number;
  studentAnswer: unknown;
  isCorrect: boolean;
}

function deltaText(value: any): string {
  if (value == null) return "";
  if (typeof value === "string") return value.replace(/\s+/g, " ").trim();
  const ops = Array.isArray(value?.ops) ? value.ops : Array.isArray(value) ? value : [];
  return ops
    .map((op: any) => (typeof op?.insert === "string" ? op.insert : ""))
    .join("")
    .replace(/\s+/g, " ")
    .trim();
}

function solutionFiles(teacherSolution: any[] | null | undefined): any[] {
  return (teacherSolution ?? [])
    .filter((block) => block && !block.isToDelete)
    .flatMap((block) => (Array.isArray(block.solution_files) ? block.solution_files : []))
    .filter((sf) => sf && !sf.isToDelete);
}

function firstPairwise(teacherSolution: any[] | null | undefined): any {
  return solutionFiles(teacherSolution)
    .map((sf) => sf.pairwise_options ?? sf.pairwiseOptions)
    .find((pw) => pw != null && !(Array.isArray(pw) && pw.length === 0));
}

function unitScore(maxPoints: number, n: number) {
  return n > 0 ? Math.floor(maxPoints / n) : 0;
}

function normalizeBoolean(value: unknown) {
  const v = String(value ?? "").trim().toLowerCase();
  if (v === "true" || v === "t") return "true";
  if (v === "false" || v === "f") return "false";
  return v;
}

function scoreClosed(problem: IProblem, studentAnswer: string): IOfflineScore | null {
  const correct = problem.close_ended?.correct_answer;
  if (correct == null || correct === "") return null;
  const isCorrect = normalizeBoolean(studentAnswer) === normalizeBoolean(correct);
  return { grade: isCorrect ? problem.max_points : 0, studentAnswer, isCorrect };
}

function scoreMultipleChoiceGraphical(
  problem: IProblem,
  teacherSolution: any[],
  studentAnswer: { student_choice?: string[] } | null,
): IOfflineScore | null {
  const pw = firstPairwise(teacherSolution);
  let correct: string[] = Array.isArray(pw?.solution) ? pw.solution.map(String) : [];
  if (!correct.length) {
    const options = problem.multiple_choice?.options ?? [];
    correct = (problem.multiple_choice?.correct_answers ?? [])
      .map((idx) => options[idx]?.key_uuid)
      .filter((k): k is string => !!k);
  }
  if (!correct.length) return null;

  const chosen = (studentAnswer?.student_choice ?? []).map(String);
  const correctSet = new Set(correct);
  const isCorrect = chosen.length === correctSet.size && chosen.every((c) => correctSet.has(c));

  return {
    grade: isCorrect ? problem.max_points : 0,
    studentAnswer: { student_choice: chosen, isCorrect },
    isCorrect,
  };
}

function normalizePairs(raw: any): Array<{ left: string; right: string }> {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((it: any) =>
      Array.isArray(it)
        ? { left: String(it[0] ?? ""), right: String(it[1] ?? "") }
        : {
            left: String(it?.leftUUID ?? it?.element ?? ""),
            right: String(it?.rightUUID ?? it?.student_choice ?? ""),
          },
    )
    .filter((p) => p.left && p.right);
}

function scoreMatchOptions(problem: IProblem, teacherSolution: any[], studentAnswer: StudentPair[]): IOfflineScore | null {
  const solution = normalizePairs(firstPairwise(teacherSolution) ?? problem.match_options?.connections);
  if (!solution.length) return null;

  const keys = new Set(solution.flatMap((p) => [`${p.left}|${p.right}`, `${p.right}|${p.left}`]));
  const annotated = (studentAnswer ?? []).map((a) => ({
    ...a,
    isCorrect: keys.has(`${a.element}|${a.student_choice}`),
  }));
  const correctCount = annotated.filter((a) => a.isCorrect).length;

  return {
    grade: correctCount * unitScore(problem.max_points, solution.length),
    studentAnswer: annotated,
    isCorrect: correctCount === solution.length && annotated.length === solution.length,
  };
}

function blankKey(attrs: any) {
  return String(attrs?.key_uuid ?? attrs?.keyUuid ?? attrs?.key ?? attrs?.id ?? "");
}

function blankMilageId(attrs: any) {
  return String(attrs?.milage_id ?? attrs?.id ?? "");
}

function flattenWordOptions(options: any): Array<{ uuid: string; text: string; milageId: string }> {
  if (!Array.isArray(options)) return [];
  return options
    .flatMap((opt: any) => (Array.isArray(opt) ? opt : Array.isArray(opt?.options) ? opt.options : [opt]))
    .filter(Boolean)
    .map((opt: any) => {
      const firstAttrs = Array.isArray(opt?.word?.ops) ? opt.word.ops.find((op: any) => op?.attributes)?.attributes : null;
      return {
        uuid: String(opt?.uuid ?? opt?.id ?? ""),
        text: deltaText(opt?.word ?? opt?.text),
        milageId: String(firstAttrs?.milage_id ?? firstAttrs?.id ?? ""),
      };
    });
}

function scoreWords(
  problem: IProblem,
  teacherSolution: any[],
  studentAnswer: StudentPair[],
  source: "drag_drop" | "word_selection",
): IOfflineScore | null {
  const data = problem[source];
  const questionOps: any[] = Array.isArray(data?.question?.ops) ? data!.question!.ops : [];
  const blanks = questionOps
    .filter((op) => op?.attributes?.milage)
    .map((op) => ({ key: blankKey(op.attributes), milageId: blankMilageId(op.attributes) }));
  if (!blanks.length) return null;

  const pw = firstPairwise(teacherSolution);
  const solutionText = new Map<string, string>();
  for (const op of Array.isArray(pw?.ops) ? pw.ops : []) {
    if (op?.attributes?.milage) solutionText.set(blankMilageId(op.attributes), deltaText(op.insert));
  }

  const options = flattenWordOptions(data?.options);
  const annotated = (studentAnswer ?? []).map((a) => {
    const blank = blanks.find((b) => b.key === String(a.element));
    if (!blank) return { ...a, isCorrect: false };
    const option = options.find((o) => o.uuid === String(a.student_choice));
    const expected = solutionText.get(blank.milageId);
    const chosenText = option ? option.text : deltaText(a.student_choice);
    const isCorrect =
      (!!option && !!option.milageId && option.milageId === blank.milageId) ||
      (expected !== undefined && chosenText === expected);
    return { ...a, isCorrect };
  });

  const correctCount = annotated.filter((a) => a.isCorrect).length;
  return {
    grade: correctCount * unitScore(problem.max_points, blanks.length),
    studentAnswer: annotated,
    isCorrect: correctCount === blanks.length,
  };
}

function scoreImageLabelling(problem: IProblem, teacherSolution: any[], studentAnswer: StudentPair[]): IOfflineScore | null {
  const pw = firstPairwise(teacherSolution);
  const solutionLabels: any[] = Array.isArray(pw?.ops) ? pw.ops : Array.isArray(pw?.op) ? pw.op : Array.isArray(pw) ? pw : [];
  const labels = solutionLabels
    .map((l) => ({ key: String(l?.key ?? l?.id ?? l?.uuid ?? ""), text: deltaText(l?.word) }))
    .filter((l) => l.key);
  if (!labels.length) return null;

  const options = [
    ...(problem.image_labels?.options ?? []).map((o: any) => ({ uuid: String(o?.uuid ?? ""), text: deltaText(o?.text ?? o?.word) })),
    ...flattenWordOptions(problem.image_labels?.extra_words).map((o) => ({ uuid: o.uuid, text: o.text })),
  ];

  const annotated = (studentAnswer ?? []).map((a) => {
    const label = labels.find((l) => l.key === String(a.element));
    if (!label) return { ...a, isCorrect: false };
    const option = options.find((o) => o.uuid === String(a.student_choice));
    const isCorrect = option ? option.text === label.text : String(a.student_choice) === label.key;
    return { ...a, isCorrect };
  });

  const correctCount = annotated.filter((a) => a.isCorrect).length;
  return {
    grade: correctCount * unitScore(problem.max_points, labels.length),
    studentAnswer: annotated,
    isCorrect: correctCount === labels.length,
  };
}

function scoreTableFill(problem: IProblem, teacherSolution: any[], studentAnswer: StudentPair[]): IOfflineScore | null {
  const solution = normalizePairs(firstPairwise(teacherSolution) ?? problem.table_fill?.connections);
  const lines = (problem.table_fill?.lines ?? []).map((l) => String(l.uuid ?? l.id));
  if (!solution.length || !lines.length) return null;

  const lineSet = new Set(lines);
  const solutionLeftIsLine = solution.some((p) => lineSet.has(p.left));
  const asLinePair = (left: string, right: string) =>
    solutionLeftIsLine ? { line: left, col: right } : { line: right, col: left };

  const expectedByLine = new Map<string, Set<string>>();
  for (const p of solution) {
    const { line, col } = asLinePair(p.left, p.right);
    if (!expectedByLine.has(line)) expectedByLine.set(line, new Set());
    expectedByLine.get(line)!.add(col);
  }

  const chosenByLine = new Map<string, Set<string>>();
  const studentPairs = (studentAnswer ?? []).map((a) => ({ ...a, ...asLinePair(String(a.element), String(a.student_choice)) }));
  for (const p of studentPairs) {
    if (!chosenByLine.has(p.line)) chosenByLine.set(p.line, new Set());
    chosenByLine.get(p.line)!.add(p.col);
  }

  const correctLines = lines.filter((line) => {
    const expected = expectedByLine.get(line) ?? new Set<string>();
    const chosen = chosenByLine.get(line) ?? new Set<string>();
    return expected.size === chosen.size && [...expected].every((c) => chosen.has(c));
  }).length;

  const annotated = studentPairs.map(({ line, col, ...a }) => ({
    ...a,
    isCorrect: expectedByLine.get(line)?.has(col) ?? false,
  }));

  return {
    grade: correctLines * unitScore(problem.max_points, lines.length),
    studentAnswer: annotated,
    isCorrect: correctLines === lines.length,
  };
}

function normalizeOrder(raw: any): string[] {
  if (!raw) return [];
  if (Array.isArray(raw) && raw.every((x) => typeof x === "string")) return raw;
  if (Array.isArray(raw) && raw.length && Array.isArray(raw[0])) {
    return raw
      .map((t: any[]) => ({ uuid: String(t[0]), order: Number(t[1] ?? 0) }))
      .sort((a, b) => a.order - b.order)
      .map((t) => t.uuid);
  }
  if (Array.isArray(raw)) {
    return raw
      .filter((x: any) => x && x.uuid != null)
      .slice()
      .sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0))
      .map((x: any) => String(x.uuid));
  }
  if (typeof raw === "object") {
    return Object.entries(raw)
      .map(([uuid, order]) => ({ uuid, order: Number(order) || 0 }))
      .sort((a, b) => a.order - b.order)
      .map((t) => t.uuid);
  }
  return [];
}

function scoreOrdering(problem: IProblem, teacherSolution: any[], studentAnswer: OrderItem[]): IOfflineScore | null {
  let correct = normalizeOrder(firstPairwise(teacherSolution));
  if (!correct.length) correct = normalizeOrder(problem.sequence_ordering?.options);
  if (!correct.length) return null;

  const ordered = (studentAnswer ?? []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const annotated = ordered.map((item, idx) => ({ ...item, isCorrect: correct[idx] === String(item.uuid) }));
  const correctCount = annotated.filter((a) => a.isCorrect).length;

  return {
    grade: correctCount * unitScore(problem.max_points, correct.length),
    studentAnswer: annotated,
    isCorrect: correctCount === correct.length,
  };
}

export function scoreOfflineAnswer(
  problem: IProblem | undefined,
  teacherSolution: any[] | null | undefined,
  studentAnswer: unknown,
): IOfflineScore | null {
  if (!problem) return null;
  const ts = teacherSolution ?? [];

  switch (problem.problem_type) {
    case ProblemType.TRUE_FALSE:
    case ProblemType.MULTIPLE_CHOICES:
      return scoreClosed(problem, String(studentAnswer ?? ""));
    case ProblemType.MULTIPLE_CHOICE:
      return scoreMultipleChoiceGraphical(problem, ts, studentAnswer as { student_choice?: string[] });
    case ProblemType.MATCH_CASE:
      return scoreMatchOptions(problem, ts, studentAnswer as StudentPair[]);
    case ProblemType.WORD_DRAG_DROP:
      return scoreWords(problem, ts, studentAnswer as StudentPair[], "drag_drop");
    case ProblemType.WORDS_SELECTION:
      return scoreWords(problem, ts, studentAnswer as StudentPair[], "word_selection");
    case ProblemType.IMAGE_LABELING:
      return scoreImageLabelling(problem, ts, studentAnswer as StudentPair[]);
    case ProblemType.TABLE_FILL:
      return scoreTableFill(problem, ts, studentAnswer as StudentPair[]);
    case ProblemType.SEQUENCE_ORDERING:
      return scoreOrdering(problem, ts, studentAnswer as OrderItem[]);
    default:
      return null;
  }
}
